import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { plantsApi } from '@/features/masters/plants/api'

/**
 * Global plant selector hook.
 *
 * DESIGN:
 *  - Persists selected plant_id to localStorage so navigating between pages
 *    NEVER resets to an empty string (which disabled all API queries).
 *  - Initialises synchronously from localStorage on mount — no blank flash.
 *  - Auto-selects first plant when no selection is stored.
 *  - Validates the stored id against the plant list (handles plant deletion).
 *
 * Previously used bare useState('') which meant every page mount started with
 * plantId='' and queries were disabled until the useEffect fired.  With 54
 * pages using this hook that caused a systematic "no data" flash on every
 * navigation and lost the user's plant choice between pages.
 */

const LS_KEY = 'erp_selected_plant_id'

function readStored(): string {
  try { return localStorage.getItem(LS_KEY) ?? '' }
  catch { return '' }
}

function writeStored(id: string): void {
  try { localStorage.setItem(LS_KEY, id) }
  catch { /* localStorage blocked in private mode */ }
}

export function usePlantId() {
  const queryClient = useQueryClient()

  // Initialise from localStorage — synchronous, no async gap on every navigation
  const [plantId, setPlantIdState] = useState<string>(() => readStored())

  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 200 }),
    staleTime: 5 * 60 * 1000, // plants list rarely changes — cache for 5 min
  })

  const plants = plantsData?.items ?? []

  useEffect(() => {
    if (plants.length === 0) return

    const validIds = new Set(plants.map((p) => p.id as string))

    if (!plantId || !validIds.has(plantId)) {
      // No selection yet, or stored plant was deleted → default to first
      const first = plants[0].id as string
      setPlantIdState(first)
      writeStored(first)
    }
  }, [plants, plantId])

  /**
   * Change the active plant.
   * Persists to localStorage AND invalidates all React Query cache so every
   * page re-fetches with the new plant_id immediately.
   */
  const setPlantId = (id: string) => {
    setPlantIdState(id)
    writeStored(id)
    // Invalidate everything EXCEPT plants-all (that doesn't filter by plant)
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] !== 'plants-all',
    })
  }

  return { plantId, setPlantId, plants }
}
